import {ChangeDetectionStrategy, Component, inject, input, output} from '@angular/core';
import {productPackageResourceType} from "../../configs/product-package-modal-config";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {productPackageFormFields} from "../../configs/product-package-form-fields";
import {productPackageFormComponents} from "../../configs/product-package-row-keys";
import {
  BaseProductPackageData,
  ProductPackageData,
  ProductPackageFormField
} from "../../types/product-package-modal.types";
import {defaultProductPackageData} from "../../configs/default-product-package";
import {ProductPackageModalService} from "../../services/product-package-modal.service";
import {RecordUpdateRequestParams, SelectData} from "../../../../../../../../core/types/records-table.types";
import {MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";
import {
  CustomMatSelectComponent
} from "../../../../../../../../shared/components/ui/custom-mat-select/custom-mat-select.component";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatButton} from "@angular/material/button";
import {DecimalLimitDirective} from "../../../../../../../../shared/directives/decimal-limit.directive";
import {ProductPackageSelectDataStateService} from "../../services/product-package-select-data-state.service";

@Component({
  selector: 'app-product-package-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    CustomMatSelectComponent,
    CdkTextareaAutosize,
    MatButton,
    MatSuffix,
    DecimalLimitDirective
  ],
  templateUrl: './product-package-form.component.html',
  styleUrls: [
    '../../product-package-modal.component.scss',
    './product-package-form.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductPackageFormComponent {
  parentId = input<number>(0);
  productPackageFormGroup = input<FormGroup>(new FormGroup({}));
  productPackageData = input<ProductPackageData>(defaultProductPackageData);
  isEditModeActive = input<boolean>(false);
  noFirstData = input<boolean>(false);
  updateProductPackageData = output<ProductPackageData>();
  deleteProductPackage = output<void>();

  private productPackageModalService: ProductPackageModalService = inject(ProductPackageModalService);
  private productPackageSelectDataStateService: ProductPackageSelectDataStateService = inject(ProductPackageSelectDataStateService);

  public readonly productPackageFormComponents = productPackageFormComponents;

  public getRowClass(row: string): string {
    const rowClasses: Record<string, string> = {
      size: 'row-size',
      articles: 'row-articles',
    };

    return rowClasses[row] || '';
  }

  public getFormFields(rowKey: string): Array<ProductPackageFormField> {
    return productPackageFormFields.filter(i => i.rowKey === rowKey);
  }

  public getFieldClass(): string {
    if (!this.productPackageData().id) { return ''; }

    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public getEditModeForSelect(): boolean {
    if (!this.productPackageData().id) { return true; }

    return this.isEditModeActive();
  }

  public async handleValueChange(key: string, value?: string): Promise<void> {
    if (!this.productPackageData().id) { return; }
    if (this.productPackageFormGroup().get(key)?.invalid) {
      this.productPackageFormGroup().get(key)?.setValue(this.productPackageData()[key])
      return;
    }

    value && this.productPackageFormGroup().get(key)?.setValue(value);
    await this.updateFormField(key);
  }

  public async handleSelectChange(value: string, key: string): Promise<void> {
    this.productPackageFormGroup().get(key)?.setValue(value);
    const name = this.productPackageFormGroup().get('packageName' as string)?.value;
    if (!name && value) {
      const valueDisplay = this.productPackageSelectDataStateService.packageTypeSelectData().find(i => String(i.id) === value)?.value;
      this.productPackageFormGroup().get('packageName' as string)?.setValue(valueDisplay);
    }

    if (!this.productPackageData().id) { return; }

    await this.updateFormField(key);
  }

  public handelDelete(): void {
    this.deleteProductPackage.emit();
  }

  public async handelCreate(): Promise<void> {
    const createData: BaseProductPackageData = {
      technicalParametersId: this.parentId(),
      ...this.productPackageFormGroup().value
    };

    const data = await this.productPackageModalService.createProductPackage(createData)
      .catch(() => defaultProductPackageData);
    this.updateProductPackageData.emit(data);
  }

  public getSelectData(key: string): Array<SelectData> {
    return this.productPackageSelectDataStateService.getSelectDataByKey(key);
  }

  public getSelectedValue(key: string): string {
    return String(this.productPackageFormGroup().get(key)?.value) || '';
  }

  private async updateFormField(key: string): Promise<void> {
    const params: RecordUpdateRequestParams = {
      path: productPackageResourceType.path,
      recordId: this.productPackageData().id,
      fieldName: key,
      value: this.productPackageFormGroup().get(key)?.value
    };

    const data = await this.productPackageModalService.updatedRecordField<ProductPackageData>(params)
      .catch(() => this.productPackageData());
    this.updateProductPackageData.emit(data);
  }
}
